/**
 * Words and sums for the campus milk card (PROD-1502): milk is its own card
 * above the mealtimes, with a badge saying how many service days the milk
 * delivered that day covers. Pure.
 */

import type { Campus, CampusMilkLine, Temperature } from './types'
import { MEALTIME_LABEL } from './types'

/**
 * Distinct SERVICE days the campus's milk rows cover: the X in "Supply for X
 * days". A campus on a weekly milk day gets its Monday drop for Monday to
 * Friday service (5); a campus with no milk day gets each day's own milk (1).
 * 0 when the campus has no milk.
 */
export function campusMilkSupplyDays(campus: Pick<Campus, 'milk'>): number {
  const days = new Set<string>()
  for (const line of campus.milk ?? []) {
    for (const day of line.serviceDates) {
      days.add(day)
    }
  }
  return days.size
}

/** "Supply for 1 day" / "Supply for 5 days". */
export function milkSupplyBadge(days: number): string {
  return `Supply for ${days} ${days === 1 ? 'day' : 'days'}`
}

/** "Breakfast 5 · Lunch 5": the per-mealtime split under a milk row. Null for one mealtime. */
export function milkMealtimeBreakdown(line: Pick<CampusMilkLine, 'byMealtime'>): string | null {
  if (line.byMealtime.length <= 1) {
    return null
  }
  return line.byMealtime.map((m) => `${MEALTIME_LABEL[m.mealtime]} ${m.qty.toLocaleString()}`).join(' · ')
}

/**
 * "PK3-5 1 · K-8 4": which age group's drop-off each milk carton goes to
 * (PROD-1624). Null when no grouped shipment fed the row. One group still
 * shows, since it names the drop-off.
 */
export function milkGroupBreakdown(line: Pick<CampusMilkLine, 'byGroup'>): string | null {
  if (!line.byGroup || line.byGroup.length === 0) {
    return null
  }
  return line.byGroup.map((g) => `${g.label} ${g.qty.toLocaleString()}`).join(' · ')
}

/** Total servings on the milk card. */
export function campusMilkTotal(campus: Pick<Campus, 'milk'>): number {
  return (campus.milk ?? []).reduce((sum, line) => sum + line.qty, 0)
}

/** One milk item summed across the day's schools. */
export interface MergedMilkRow {
  key: string
  name: string
  qty: number
  servingSize?: string
  packaging?: string
  temperature: Temperature
  /** Schools taking it. */
  schools: number
}

/**
 * The day's milk, one row per milk item (`CampusMilkLine.key`) summed across schools, sorted by name: the
 * Deliverables tab and the printed Deliverables page (milk left the mealtimes for the campus card, PROD-1502).
 */
export function mergeCampusesMilk(campuses: readonly Campus[]): MergedMilkRow[] {
  const rows = new Map<string, MergedMilkRow>()
  for (const campus of campuses) {
    for (const line of campus.milk ?? []) {
      const row = rows.get(line.key)
      if (row) {
        row.qty += line.qty
        row.schools += 1
      } else {
        rows.set(line.key, {
          key: line.key,
          name: line.name,
          qty: line.qty,
          servingSize: line.servingSize,
          packaging: line.packaging,
          temperature: line.temperature,
          schools: 1,
        })
      }
    }
  }
  return [...rows.values()].sort((a, b) => a.name.localeCompare(b.name))
}
